"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.widgetMessageSchema = exports.hostMessageSchema = exports.widgetActionMessageSchema = exports.widgetTokenMessageSchema = exports.widgetInitMessageSchema = exports.widgetActionSchema = exports.WIDGET_TO_HOST = exports.HOST_TO_WIDGET = exports.embedTokenResponseSchema = exports.embedTokenRequestSchema = void 0;
/**
 * Wire types for `/embed/v1`, the token brokering surface (section 14).
 *
 * One endpoint, and the only caller is a back end: the CRM's server exchanges the representative's
 * provider bearer for a token scoped to one exchange, and posts that into the iframe. No browser ever
 * calls this, which is why there is no CORS story for it and why the request carries a contact
 * reference rather than an exchange id - the host knows who the rep is looking at and does not know
 * our identifiers.
 *
 * Imports nothing internal: Zod and TypeScript only, enforced by the zone rule.
 */
const zod_1 = require("zod");
/**
 * Exactly one of the two references.
 *
 * `contactExternalRef` is the one a real integration uses, because `contact.external_ref` exists for
 * the CRM's own identifier and a host back end holds that rather than ours. `contactId` is here for
 * the demo host and for a CRM that has stored our id against its record.
 *
 * Both, or neither, is a 400 rather than a precedence rule. A caller that sent both would have to be
 * told which one won, and the answer would be a silent behaviour nobody documented.
 */
exports.embedTokenRequestSchema = zod_1.z
    .discriminatedUnion('type', [
    zod_1.z
        .object({
        type: zod_1.z.literal('secure_documents'),
        contactId: zod_1.z.uuid().optional(),
        contactExternalRef: zod_1.z.string().min(1).max(200).optional(),
    })
        .strict()
        .refine((value) => (value.contactId === undefined) !== (value.contactExternalRef === undefined), { message: 'Name a contact by id or by external reference, and not both.' }),
    zod_1.z
        .object({
        type: zod_1.z.literal('rep_tools'),
    })
        .strict(),
])
    .or(zod_1.z
    .object({
    contactId: zod_1.z.uuid().optional(),
    contactExternalRef: zod_1.z.string().min(1).max(200).optional(),
})
    .strict()
    .refine((value) => (value.contactId === undefined) !== (value.contactExternalRef === undefined), { message: 'Name a contact by id or by external reference, and not both.' })
    .transform((value) => ({ type: 'secure_documents', ...value })));
/**
 * What the host posts into the frame, minus the token.
 *
 * `expiresIn` is seconds, matching figure 8 and OAuth's own convention, and it is what the widget
 * counts down from to ask for a replacement about sixty seconds early. An absolute instant would be
 * wrong here: the host and the browser do not share a clock, and a widget comparing a server's
 * timestamp against a laptop set five minutes fast would refresh constantly or never.
 *
 * `exchangeId` is included because the widget displays it in nothing and needs it for everything: it
 * is the id in every `/api/v1` path the widget calls. The token is scoped to it server-side, so
 * sending it is a convenience rather than a grant.
 */
exports.embedTokenResponseSchema = zod_1.z.object({
    token: zod_1.z.string().min(1),
    expiresIn: zod_1.z.number().int().positive(),
    exchangeId: zod_1.z.uuid().optional(),
});
/**
 * The `iqz-ddds:` postMessage protocol, as types both ends share.
 *
 * Section 14 gives these six verbatim. The prefix matches the `@iqz-ddds/*` workspace scope and is
 * part of a contract with the host page, so changing it is a coordinated release with the CRM team
 * rather than a rename.
 *
 * They live in `shared/contracts` rather than in the widget because the *host* needs them too - and a
 * host that is not us cannot import from here at all, which is the point of writing them down. The
 * demonstration CRM is the case in hand: it duplicates these constants in plain browser JavaScript
 * because it has no build step, so it is the reason the protocol is a documented artefact rather than
 * a shared import. An earlier version of this comment claimed it imported them, and it does not.
 */
exports.HOST_TO_WIDGET = {
    /** First message. Carries the brokered token, and nothing works before it arrives. */
    init: 'iqz-ddds:init',
    /** A replacement token, in response to `expiring`. */
    token: 'iqz-ddds:token',
    /**
     * Drive a control the panel is no longer drawing.
     *
     * A host that sets `chrome: 'none'` renders its own buttons - most CRMs have a way to define one
     * that runs a line of JavaScript - and this is how those buttons reach the panel. One message with
     * an action name rather than one message per action, so a third control is a new member of the union
     * rather than a new message type and another coordinated release.
     */
    action: 'iqz-ddds:action',
};
exports.WIDGET_TO_HOST = {
    /**
     * The frame is listening, and this is the host's cue to post the token.
     *
     * Not merely informational, which an earlier version of this comment claimed. `postMessage` is not queued,
     * so an `init` sent before the frame attached its listener is lost - and the frame attaches it after React
     * mounts and after fetching its runtime configuration, which is well after the iframe's own `load` event.
     * A host that posts on `load` is too early. Demonstrated in a browser, not reasoned about.
     */
    ready: 'iqz-ddds:ready',
    /** Content height, so the host can size the iframe. Sent whenever it changes. */
    resize: 'iqz-ddds:resize',
    /**
     * About sixty seconds before the token lapses, when the host sent `expiresIn` on `init`. The host
     * brokers a new one. A host that sent no lifetime gets a fixed minute after each token instead,
     * because the frame has nothing to count back from; see `expiresIn`.
     */
    expiring: 'iqz-ddds:expiring',
    /** Something failed in a way the host should know about. Never carries a token. */
    error: 'iqz-ddds:error',
};
/**
 * What the panel may be asked to do from outside it.
 *
 * Deliberately verbs about intent rather than about controls: `request` is "ask this person for a
 * document" and `send` is "give them one", which is what a representative means whichever button they
 * pressed. A host that renames its buttons does not need us to release anything.
 */
exports.widgetActionSchema = zod_1.z.enum(['request', 'send']);
/**
 * Every optional field below carries `.catch(undefined)`, and that is a fix rather than a style.
 *
 * `hostMessageSchema` is a discriminated union, so a field that failed validation used to fail the
 * whole message - and the whole message is where the token is. A host sending `theme: "auto"` or a
 * one-character `locale` therefore lost its session and read "not given access by the page containing
 * it", which is a sentence about origins for a fault that was neither about origins nor about access
 * (DG.38). An out-of-range optional field now costs that field and nothing else.
 *
 * `type` and `token` are deliberately excluded: the discriminator and the credential are what the
 * message *is*, and a message missing either is not a message we should act on. The residual cost is
 * that a bad optional value is now ignored silently, which the integration guide states rather than
 * hides - reporting it would mean parsing twice and inventing an error code for a typo.
 */
exports.widgetInitMessageSchema = zod_1.z.object({
    type: zod_1.z.literal(exports.HOST_TO_WIDGET.init),
    token: zod_1.z.string().min(1),
    /**
     * How long the token above is good for, in seconds, as the broker reported it.
     *
     * Without it the frame cannot know, so `iqz-ddds:expiring` fired a fixed minute *after* every token
     * rather than a minute before it lapsed - at the shipped 900-second TTL, fifteen times more often
     * than section 14 claims and than a host would budget for (DG.38). Send what your broker returned.
     * A host that omits it keeps the old fixed cadence, which is what makes this non-breaking.
     *
     * On `init` only. Every mint carries the same configured lifetime, so a replacement token reuses
     * the one learned here; if that ever stops being true, adding it to `iqz-ddds:token` is additive.
     */
    expiresIn: zod_1.z.number().int().positive().optional().catch(undefined),
    /** One language ships; i18n is not in scope. Present so the contract does not change later. */
    locale: zod_1.z.string().min(2).max(35).optional().catch(undefined),
    /** The host's colour scheme. An embedded panel follows the application it is embedded in. */
    theme: zod_1.z.enum(['light', 'dark']).optional().catch(undefined),
    exchangeId: zod_1.z.uuid().optional().catch(undefined),
    /**
     * Whether the panel draws its own header, with the contact's name and the two primary actions.
     *
     * `full` is the default and is right for a host that gives the widget a pane and leaves it alone.
     * `none` is for a host that already shows the contact at the top of its own record and has its own
     * Send and Request buttons - drawing ours underneath theirs would be the same person's name twice
     * and four buttons for two intentions.
     *
     * Choosing `none` is a commitment rather than a preference: the panel then has no way to start
     * either action on its own, so the host must send `iqz-ddds:action` or a representative simply
     * cannot ask for or send anything.
     */
    chrome: zod_1.z.enum(['full', 'none']).optional().catch(undefined),
    /**
     * Which way the panel's tab strip runs.
     *
     * A *preference* rather than a guarantee, and the contract says so because a host cannot know
     * otherwise: a column needs width the panel does not control, and a host gives us whatever pane it
     * has. Below 60rem of frame width the panel renders a horizontal strip whatever this says. Absent
     * means horizontal, so a host written before this is unaffected.
     */
    tabs: zod_1.z.enum(['horizontal', 'vertical']).optional().catch(undefined),
    /**
     * Which tab opens.
     *
     * Honoured when the panel first shows an exchange and not afterwards, which matters because a host
     * is told to repeat every field on every re-post and a theme change is a documented reason to
     * re-post: without that rule, switching the CRM to dark mode would drag a representative from the
     * trail they were reading back to the checklist. Absent means the panel chooses.
     */
    view: zod_1.z.enum(['requested', 'received', 'sent', 'activity']).optional().catch(undefined),
});
exports.widgetTokenMessageSchema = zod_1.z.object({
    type: zod_1.z.literal(exports.HOST_TO_WIDGET.token),
    token: zod_1.z.string().min(1),
});
exports.widgetActionMessageSchema = zod_1.z.object({
    type: zod_1.z.literal(exports.HOST_TO_WIDGET.action),
    action: exports.widgetActionSchema,
});
/**
 * Anything the host may send.
 *
 * A discriminated union so an unrecognised `type` fails validation rather than being ignored: the
 * widget's message handler runs on every `message` event the frame receives, including from scripts
 * on the host page that have nothing to do with us.
 */
exports.hostMessageSchema = zod_1.z.discriminatedUnion('type', [
    exports.widgetInitMessageSchema,
    exports.widgetTokenMessageSchema,
    exports.widgetActionMessageSchema,
]);
exports.widgetMessageSchema = zod_1.z.discriminatedUnion('type', [
    zod_1.z.object({ type: zod_1.z.literal(exports.WIDGET_TO_HOST.ready) }),
    zod_1.z.object({ type: zod_1.z.literal(exports.WIDGET_TO_HOST.resize), height: zod_1.z.number().int().nonnegative() }),
    zod_1.z.object({ type: zod_1.z.literal(exports.WIDGET_TO_HOST.expiring) }),
    zod_1.z.object({
        type: zod_1.z.literal(exports.WIDGET_TO_HOST.error),
        /** A stable slug, never a sentence: the host may want to act on it. */
        code: zod_1.z.string().min(1),
        /**
         * The identifier a customer reads back to us over the phone (section 13). Included here
         * deliberately: an error inside an iframe is invisible in the host's own logs otherwise.
         */
        correlationId: zod_1.z.string().min(1).optional(),
    }),
]);
//# sourceMappingURL=embed.js.map